import type { QuoteLineOrigin, VatOptionDto } from './types'

const LANGUAGES: Record<string, string> = {
  fr: 'Français',
  nl: 'Néerlandais',
  de: 'Allemand',
  en: 'Anglais',
  it: 'Italien',
}

const euro = new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' })
const number = new Intl.NumberFormat('fr-BE', { maximumFractionDigits: 4 })

export function isImportedLine(line: { origin?: QuoteLineOrigin | null }): boolean {
  return line.origin != null
}

/** « Réf. H5022101 · HÄCKER Concept130 2024-1 FR Cuisines »: shown beside the line, never printed. */
export function originSummary(origin: QuoteLineOrigin): string {
  const parts = [origin.reference ? `Réf. ${origin.reference}` : null, origin.catalog || null]
  return parts.filter(Boolean).join(' · ')
}

export interface OriginFact {
  label: string
  value: string
}

/** What the file said about the line, in the order a line is read; a fact the file left out is not listed. */
export function originFacts(origin: QuoteLineOrigin): OriginFact[] {
  const facts: OriginFact[] = []
  if (origin.text) facts.push({ label: 'Texte', value: origin.text })
  if (typeof origin.unitPrice === 'number') facts.push({ label: 'Prix HT', value: euro.format(origin.unitPrice) })
  if (typeof origin.quantity === 'number')
    facts.push({ label: 'Quantité', value: [number.format(origin.quantity), origin.unit].filter(Boolean).join(' ') })
  if (origin.position) facts.push({ label: 'Position', value: origin.position })
  if (origin.groups.length > 0) facts.push({ label: 'Groupe', value: origin.groups.join(', ') })
  if (origin.language) facts.push({ label: 'Langue', value: LANGUAGES[origin.language] ?? origin.language })
  return facts
}

/**
 * The project's VAT rates, plus the line's own rates when the project does not offer them (a Winner
 * line at 12 %): the rate it opened with and the one now picked, so the select always shows what
 * the line has and the user can come back to where it started.
 */
export function vatOptionsFor(offered: VatOptionDto[], ...lineRates: (number | null | undefined)[]): VatOptionDto[] {
  const missing = [...new Set(lineRates)]
    .filter((rate): rate is number => typeof rate === 'number' && !offered.some((o) => o.value === rate))
  if (missing.length === 0) return offered
  return [...offered, ...missing.map((rate) => ({ value: rate, label: `${rate}%` }))].sort((a, b) => a.value - b.value)
}

/** The quote PDF and the final invoice print a whole number of pieces: 1,5 would print as 1. */
export function isWholeQuantity(quantity: number | null | undefined): boolean {
  return typeof quantity === 'number' && Number.isInteger(quantity) && quantity > 0
}
